import { useRouter } from '@application/utils';
import {
  BREEDER_PROFILE_TOTAL_STEPS,
  Container,
  MtOnBoardingCard,
  Section,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { BsPersonCheckFill } from 'react-icons/bs';
import { FaDog, FaPaw } from 'react-icons/fa';
import { MtAnnouncements } from './components/Announcements';
import { Progress } from './components/Progress';
import * as Styled from './styled';
import type { MtLandingBreederProps } from './types';

export * from './types';

export const MtLandingBreederPage: FC<MtLandingBreederProps> = (props) => {
  const {
    currentStep,
    currentProgress,
    onCompleteProfileClick,
    loading = false,
    hasAddedAnimals,
    hasAddedLitters,
    profileValidated,
    announcements,
    onLoadMoreAnnouncementsClick,
    announcementsLoading,
    canLoadMoreAnnouncements,
    loadMoreAnnouncementsLoading,
    onboardingStep,
  } = props;
  const { navigateToRoute } = useRouter();
  const { translate } = useTranslation({ keyPrefix: 'mt.breeder' });

  const progress = Math.floor((currentProgress / BREEDER_PROFILE_TOTAL_STEPS) * 100);

  return (
    <Container>
      <Styled.TopWrapper>
        <Styled.TopWrapperColumn width={60}>
          <Styled.Text>{translate(`onboarding.steps.${onboardingStep}.title`)}</Styled.Text>
          <Styled.SubText>{translate(`onboarding.steps.${onboardingStep}.text`)}</Styled.SubText>
        </Styled.TopWrapperColumn>

        <Styled.TopWrapperColumn width={40}>
          <Styled.ProgressBarHeader>
            <Styled.ProgressBarTitle>
              {translate('onboarding.steps.progressLabel')}
            </Styled.ProgressBarTitle>
            <Styled.ProgressBarValue full={progress === 100}>{progress}%</Styled.ProgressBarValue>
          </Styled.ProgressBarHeader>
          <Styled.ProgressBarWrapper>
            <Progress total={BREEDER_PROFILE_TOTAL_STEPS} current={currentProgress} />
          </Styled.ProgressBarWrapper>
        </Styled.TopWrapperColumn>
      </Styled.TopWrapper>

      <Styled.Divider />

      <Styled.CardsWrapper>
        <MtOnBoardingCard
          title={translate('onboarding.steps.cards.1.title')}
          text={translate('onboarding.steps.cards.1.text')}
          icon={<BsPersonCheckFill />}
          btnText={translate('onboarding.steps.cards.1.button')}
          btnOnClick={onCompleteProfileClick}
          btnDisabled={currentStep > 1} // Finished questionnaire
          btnLoading={loading}
          checked={profileValidated}
        />

        <MtOnBoardingCard
          title={translate('onboarding.steps.cards.2.title')}
          text={translate('onboarding.steps.cards.2.text')}
          icon={<FaDog />}
          btnText={translate('onboarding.steps.cards.2.button')}
          btnOnClick={() => navigateToRoute('mtAnimals', { params: ['new'] })}
          btnDisabled={currentStep === 1}
          checked={hasAddedAnimals}
        />
        <MtOnBoardingCard
          title={translate('onboarding.steps.cards.3.title')}
          text={translate('onboarding.steps.cards.3.text')}
          icon={<FaPaw />}
          btnText={translate('onboarding.steps.cards.3.button')}
          btnOnClick={() => navigateToRoute('mtLitters', { params: ['new'] })}
          btnDisabled={currentStep === 1}
          checked={hasAddedLitters}
        />
      </Styled.CardsWrapper>

      {announcements && announcements.length > 0 && (
        <Section>
          <MtAnnouncements
            announcements={announcements}
            announcementsLoading={announcementsLoading}
            onLoadMoreAnnouncementsClick={onLoadMoreAnnouncementsClick}
            canLoadMoreAnnouncements={canLoadMoreAnnouncements}
            loadMoreAnnouncementsLoading={loadMoreAnnouncementsLoading}
          />
        </Section>
      )}
    </Container>
  );
};
